import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { When } from 'react-if';
import { toast } from 'react-toastify';
import { DateTime } from 'luxon';
import { Tooltip } from '@mui/material';
import { ArrowPathIcon, PhoneIcon } from '@heroicons/react/20/solid';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import {
  MixpanelEvents,
  OrganisationFeatureFlags,
  statusUIMapXero,
} from 'shared';

import { PillBadge } from '../../../../common/Atoms/PillBadge';
import { ToolbarButton } from '../../../../common/Atoms/ToolbarButton';
import { UnderlineTabs } from '../../../../common/Atoms/UnderlineTabs';
import { DatePopover } from '../../../../common/Components/DatePopover';
import { useInvoiceDetails } from '../../../../common/hooks/useInvoiceDetails';
import { env } from '../../../../common/lib/env';
import { mixpanelService } from '../../../../common/lib/mixpanel';
import { iconMap } from '../../../constants/iconMap';
import { useAccountingSystemName } from '../../../hooks/useAccountingSystemName';
import { useAdhocActionModal } from '../../../hooks/useAdhocActionModal';
import { useAllowedFeature } from '../../../hooks/useAllowedFeature';
import { useEscalations } from '../../../hooks/useEscalations';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useInvoice } from '../../../hooks/useInvoice';
import { useActionInvoiceMutation, useFetchSavedCardsQuery } from '../../../services/api/invoiceApi/invoice';
import { RaiseEscalationModal } from '../../Escalations/RaiseEscalationModal';
import { FrozenToggle } from '../../Molecules/FrozenToggle';
import { UpgradeBadge } from '../../Molecules/UpgradeBadge';
import { WrappedLink } from '../../WrappedLink';
import { InvoicePaymentPlanModal } from '../InvoiceDocument/InvoicePaymentPlanModal';
import { PayWithSavedCardModal } from '../PayWithSavedCardModal';

import { INVOICE_COLUMN_ID } from './InvoiceOverviewV2';
import { useActivePaymentPlan } from './InvoicePaymentPlanStrip';

const tabs = [
  { name: `Invoice`, href: `` },
  { name: `Actions`, href: `actions` },
  { name: `Preview Policies`, href: `policy-preview` },
  { name: `Portal Views`, href: `analytics` },
];

const linkClass = `px-1.5 text-[0.8125rem] text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline`;

export function InvoiceHeaderV2() {
  const { organisationId, invoiceId } = useParams();
  const organisation = useGetSelectedOrganisation();
  const { invoice } = useInvoice();
  const invoiceDetails = useInvoiceDetails(invoice);
  const { data: escalationData } = useEscalations();
  const [actionInvoice, { isLoading: actionInvoiceLoading }] = useActionInvoiceMutation();
  const { openSendInvoiceEmail, openIssueLateFee, openSendSms } = useAdhocActionModal();
  const featureFlags = useFeatureFlags();
  const allowedPaymentPlans = useAllowedFeature(`paymentPlans`);
  const accountingSystemName = useAccountingSystemName();
  const [paymentPlanOpen, setPaymentPlanOpen] = useState(false);
  const [savedCardOpen, setSavedCardOpen] = useState(false);
  const [raiseEscalationOpen, setRaiseEscalationOpen] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const paymentPlan = useActivePaymentPlan();
  const navigate = useNavigate();
  const { data: savedCardQuote } = useFetchSavedCardsQuery({ organisationId, invoiceId }, {
    skip: !organisation?.featureFlags?.includes(OrganisationFeatureFlags.PORTAL_PAYMENT_METHODS) || invoice?.xero_status !== `AUTHORISED`,
  });

  if (!invoice || !invoiceDetails) return null;

  const timezone = organisation.validatedTimezone;
  const unpaid = invoice.xero_status === `AUTHORISED`;
  const escalation = escalationData?.escalations?.[0];
  const overdueDays = unpaid && invoiceDetails.dueDateString ?
    Math.floor(DateTime.now().setZone(timezone).startOf(`day`)
      .diff(DateTime.fromISO(invoiceDetails.dueDateString, { zone: timezone }).startOf(`day`), `days`).days) :
    0;

  function onFrozenToggle() {
    actionInvoice({ invoiceId, organisationId, action: invoiceDetails.frozen ? `reset` : `freeze` });
  }

  function onPaymentPlanBadge() {
    const column = document.getElementById(INVOICE_COLUMN_ID);

    if (!column) {
      // On another tab, go to the invoice
      navigate(`.`);

      return;
    }

    column.scrollTo({ top: 0, behavior: `smooth` });
    column.scrollIntoView({ behavior: `smooth`, block: `start` });
  }

  async function onResync() {
    setResyncing(true);
    await actionInvoice({ invoiceId, organisationId, action: `resync` });
    setResyncing(false);
  }

  function onSendReminder() {
    openSendInvoiceEmail(invoiceId, invoiceDetails.number);
    mixpanelService.trackMixpanelEvent(MixpanelEvents.InvoicePageSendReminderNowClick);
  }

  function onSendSms() {
    openSendSms(invoiceId, invoiceDetails.number);
    mixpanelService.trackMixpanelEvent(MixpanelEvents.InvoicePageSmsNowClick);
  }

  function onIssueLateFee() {
    openIssueLateFee(invoiceId, invoiceDetails.number);
    mixpanelService.trackMixpanelEvent(MixpanelEvents.InvoicePageIssueLateFeeNowClick);
  }

  function onCreatePaymentPlan() {
    if (!allowedPaymentPlans) {
      toast.error(`Payment plans are only available on a "Pro" subscription or higher.`);

      return;
    }

    setPaymentPlanOpen(true);
  }

  const SendReminderEmailIcon = iconMap.sendReminderEmail;
  const SendSmsIcon = iconMap.sendSms;
  const IssueLateFeeIcon = iconMap.issueLateFee;
  const CreatePaymentPlanIcon = iconMap.createPaymentPlan;
  const RaiseEscalationIcon = iconMap.raiseEscalation;
  const PayWithSavedCardIcon = iconMap.payWithSavedCard;

  return (
    // Full width bar under the top nav. Cancels the page container's side padding (px-4 sm:px-6 md:px-8)
    // and puts it back inside, so the title, toolbar and tabs line up with the cards below
    <div className={ `-mx-4 flex shrink-0 flex-col gap-3 bg-white px-4 pt-4 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8` }>
      <div className={ `flex flex-wrap items-center justify-between gap-6` }>
        <div className={ `flex min-w-0 flex-col gap-1` }>
          <div className={ `flex flex-wrap items-center gap-2.5` }>
            <h1 className={ `text-xl font-semibold text-gray-900` }>
              { invoiceDetails.number ? `Invoice #${invoiceDetails.number}` : `Invoice` }
            </h1>
            <PillBadge>
              { statusUIMapXero[invoiceDetails.status]?.name || `Unknown Status` }
            </PillBadge>
            <When condition={ !!escalation }>
              { () => (
                <WrappedLink
                  to={ `/escalations/${escalation.id}/contact/${escalation.contactId}` }
                  className={ `hover:underline` }
                >
                  <PillBadge
                    color={ `red` }
                    icon={ PhoneIcon }
                  >
                    { `Escalated` }
                  </PillBadge>
                </WrappedLink>
              ) }
            </When>
            <When condition={ !!paymentPlan }>
              <button
                type={ `button` }
                onClick={ onPaymentPlanBadge }
                className={ `cursor-pointer hover:underline` }
              >
                <PillBadge
                  color={ `paidniceGreen` }
                  icon={ CalendarDaysIcon }
                >
                  { `Payment plan` }
                </PillBadge>
              </button>
            </When>
            <Tooltip title={ `Force a resync of this invoice with your accounting software.` }>
              <button
                type={ `button` }
                aria-label={ `Resync invoice` }
                onClick={ onResync }
                disabled={ resyncing }
                className={ `cursor-pointer rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed` }
              >
                <ArrowPathIcon className={ `h-4 w-4 ${resyncing ? `animate-spin` : ``}` } />
              </button>
            </Tooltip>
          </div>
          <div className={ `text-[0.8125rem] text-gray-500` }>
            { invoiceDetails.contact?.name }
            { ` · ` }
            <span className={ `font-semibold text-gray-900` }>{ invoiceDetails.dueAmount }</span>
            { ` due ` }
            <DatePopover
              disablePopover
              date={ invoiceDetails.dueDateString }
              labelFormat={ `d LLL yyyy` }
              organisationTimezone={ timezone }
              time={ `end` }
            />
            <When condition={ overdueDays > 0 }>
              { ` · ` }
              <span className={ `text-red-700` }>
                { `${overdueDays} ${overdueDays === 1 ? `day` : `days`} overdue` }
              </span>
            </When>
          </div>
        </div>

        <FrozenToggle
          value={ invoiceDetails.frozen || false }
          onChange={ onFrozenToggle }
          name={ `skip` }
          activeText={ `Invoice frozen` }
          inactiveText={ `Freeze invoice` }
          disabled={ actionInvoiceLoading }
          tooltip={ `Freezing prevents any further processing on this invoice` }
        />
      </div>

      { /* Quick actions */ }
      <div className={ `flex flex-wrap items-center gap-1.5` }>
        <ToolbarButton
          primary
          onClick={ onSendReminder }
          icon={ SendReminderEmailIcon }
        >
          { `Send reminder` }
        </ToolbarButton>
        <ToolbarButton
          onClick={ onSendSms }
          icon={ SendSmsIcon }
        >
          { `SMS` }
        </ToolbarButton>
        <ToolbarButton
          onClick={ onIssueLateFee }
          icon={ IssueLateFeeIcon }
        >
          { `Late fee` }
        </ToolbarButton>
        <When condition={ !!featureFlags?.paymentPlans && unpaid }>
          <ToolbarButton
            onClick={ onCreatePaymentPlan }
            icon={ CreatePaymentPlanIcon }
          >
            { `Payment plan` }
            <When condition={ !allowedPaymentPlans }>
              <UpgradeBadge />
            </When>
          </ToolbarButton>
        </When>
        <ToolbarButton
          onClick={ () => setRaiseEscalationOpen(true) }
          icon={ RaiseEscalationIcon }
        >
          { `Escalate` }
        </ToolbarButton>
        <When condition={ !!savedCardQuote?.paymentMethods.length }>
          <ToolbarButton
            onClick={ () => setSavedCardOpen(true) }
            icon={ PayWithSavedCardIcon }
          >
            { `Pay with saved card` }
          </ToolbarButton>
        </When>

        <span className={ `mx-1.5 h-5 w-px bg-gray-200` } />

        <When condition={ !!invoiceDetails.externalLink }>
          <a
            href={ invoiceDetails.externalLink }
            target={ `_blank` }
            rel={ `noopener noreferrer` }
            className={ linkClass }
          >
            { `View in ${accountingSystemName} ↗` }
          </a>
        </When>
        <a
          href={ `${env.portalWebUrl}/i/${invoiceDetails.hash}` }
          target={ `_blank` }
          rel={ `noopener noreferrer` }
          className={ linkClass }
        >
          { `Customer portal ↗` }
        </a>
      </div>

      <UnderlineTabs
        dense
        tabs={ tabs }
        className={ `-mx-4 px-4 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8` }
      />

      <InvoicePaymentPlanModal
        open={ paymentPlanOpen }
        onClose={ () => setPaymentPlanOpen(false) }
      />

      { savedCardQuote && (
        <PayWithSavedCardModal
          open={ savedCardOpen }
          onClose={ () => setSavedCardOpen(false) }
          quote={ savedCardQuote }
          invoiceNumber={ invoiceDetails.number }
          onSendReminder={ onSendReminder }
        />
      ) }

      <RaiseEscalationModal
        open={ raiseEscalationOpen }
        setOpen={ setRaiseEscalationOpen }
        contact={ { id: invoice.contact.id, name: invoice.contact.name } }
        invoiceId={ invoice.id }
      />
    </div>
  );
}
